/**
 * WorkspaceMembersTab Component
 *
 * Tab for managing workspace members
 * Includes token management functionality
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Workspace, WorkspaceMember } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Trash2, Edit2, Shield, Mail, Zap, Search } from 'lucide-react';
import { EditMemberPermissionsModal } from './EditMemberPermissionsModal';
import { InviteMemberModal } from './InviteMemberModal';
import { PendingInvitesList } from './PendingInvitesList';
import { ConfirmModal } from './ConfirmModal';
import { TokenManagementModal } from '../../common/modals/TokenManagementModal';
import { Pagination } from '../../common/components/Pagination';
import * as userService from '../../../services/userService';
import { User } from '../../../types/user';

interface WorkspaceMembersTabProps {
  workspace: Workspace;
}

export const WorkspaceMembersTab: React.FC<WorkspaceMembersTabProps> = ({ workspace }) => {
  const { loadWorkspaceMembers, removeWorkspaceMember } = useWorkspace();
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState<WorkspaceMember | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<number | null>(null);
  const [refreshInvites, setRefreshInvites] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<WorkspaceMember | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Token management state
  const [tokenModalMember, setTokenModalMember] = useState<WorkspaceMember | null>(null);

  // Pagination and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Check if current user can manage tokens
  const canManageTokens = (): boolean => {
    if (!currentUser) return false;

    // Superadmins can always manage tokens
    if (currentUser.isSuperAdmin) return true;

    // Workspace owners can manage tokens
    if (workspace.owner_id === currentUser.id) return true;

    // Users with can_admin or can_manage_workspace can manage tokens
    if (currentUser.canAdmin || currentUser.canManageWorkspace) return true;

    return false;
  };

  const loadMembers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await loadWorkspaceMembers(workspace.id);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar membros');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [workspace.id]);

  const handleRemoveMemberClick = (member: WorkspaceMember) => {
    setMemberToDelete(member);
    setShowDeleteConfirm(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;

    setDeletingMemberId(memberToDelete.user_id);
    setErrorMessage(null);

    try {
      await removeWorkspaceMember(workspace.id, memberToDelete.user_id);
      setMembers(prev => prev.filter(m => m.user_id !== memberToDelete.user_id));
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Falha ao remover membro');
    } finally {
      setDeletingMemberId(null);
    }
  };

  const handleUpdateTokenLimit = async (userId: number, newLimit: number | null) => {
    try {
      await userService.updateUserTokenLimit(userId, newLimit);
      // Reload members to get updated token data
      await loadMembers();
    } catch (err) {
      console.error('Error updating token limit:', err);
      throw err;
    }
  };

  const handleResetTokens = async (userId: number) => {
    try {
      await userService.resetUserTokens(userId);
      // Reload members to get updated token data
      await loadMembers();
    } catch (err) {
      console.error('Error resetting tokens:', err);
      throw err;
    }
  };

  const getPermissionsList = (member: WorkspaceMember): string[] => {
    const permissions: string[] = [];
    if (member.permissions.can_admin) permissions.push('Administrador');
    if (member.permissions.can_view_analytics) permissions.push('Ver Analytics');
    if (member.permissions.can_manage_agents) permissions.push('Gerenciar Agentes');
    if (member.permissions.can_manage_workspace) permissions.push('Gerenciar Workspace');
    return permissions.length > 0 ? permissions : ['Sem permissões'];
  };

  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(0)}k`;
    }
    return tokens.toString();
  };

  // Filter members by search term
  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return members;

    const lowerSearch = searchTerm.toLowerCase();
    return members.filter((member) => {
      const name = (member.user_name || member.user?.name || '').toLowerCase();
      const email = (member.user_email || member.user?.email || '').toLowerCase();
      return name.includes(lowerSearch) || email.includes(lowerSearch);
    });
  }, [members, searchTerm]);

  // Get current page members
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMembers.slice(startIndex, endIndex);
  }, [filteredMembers, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F4CC04]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {(error || errorMessage) && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error || errorMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Membros do Workspace</h3>
          <p className="text-sm text-[#888888] mt-1">
            Gerenciar acesso ao workspace ({members.length} membro{members.length !== 1 ? 's' : ''})
          </p>
        </div>

        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
        >
          <Mail className="w-4 h-4" />
          <span>Convidar por Email</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-[#888888]" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nome ou email..."
          className="w-full pl-10 pr-4 py-2 bg-[#1a1a1a] border border-[#333333] rounded-lg text-white placeholder-[#888888] focus:outline-none focus:border-[#F4CC04] transition-colors"
        />
        {searchTerm && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={() => setSearchTerm('')}
              className="text-[#888888] hover:text-white transition-colors"
              title="Limpar busca"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Pending Invites Section */}
      <div>
        <h4 className="text-md font-semibold text-white mb-3">Convites Pendentes</h4>
        <PendingInvitesList
          key={refreshInvites}
          workspaceId={workspace.id}
          onInviteCancelled={() => setRefreshInvites(prev => prev + 1)}
        />
      </div>

      {/* Members List */}
      {filteredMembers.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-[#888888] mx-auto mb-3" />
          {searchTerm ? (
            <>
              <p className="text-[#888888] mb-2">Nenhum membro encontrado para "{searchTerm}"</p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-[#F4CC04] hover:text-[#D4B104] font-medium transition-colors"
              >
                Limpar busca
              </button>
            </>
          ) : (
            <>
              <p className="text-[#888888] mb-2">Nenhum membro neste workspace ainda</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="mt-4 text-[#F4CC04] hover:text-[#D4B104] font-medium transition-colors"
              >
                Convide o primeiro membro por email
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#333333]">
            <thead className="bg-[#0a0a0a]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                  Permissões
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                  Limite de Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                  Adicionado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#888888] uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1a1a1a] divide-y divide-[#333333]">
              {paginatedMembers.map((member) => {
                const permissions = getPermissionsList(member);
                const isOwner = member.user_id === workspace.owner_id;
                const tokenUsed = member.token_used || 0;
                const tokenLimit = member.token_limit !== undefined ? member.token_limit : null;
                const usagePercentage = tokenLimit !== null && tokenLimit !== undefined && tokenLimit > 0
                  ? Math.min(100, (tokenUsed / tokenLimit) * 100)
                  : 0;

                return (
                  <tr key={member.id} className="hover:bg-[#2a2a2a] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-white">{member.user_name || member.user?.name || 'Desconhecido'}</div>
                        <div className="text-sm text-[#888888]">{member.user_email || member.user?.email || 'N/A'}</div>
                      </div>
                      {isOwner && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400 mt-1">
                          Proprietário
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {permissions.map((perm, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#F4CC04]/20 text-[#F4CC04]"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">
                            {formatTokens(tokenUsed)} / {tokenLimit === null || tokenLimit === undefined ? '∞' : formatTokens(tokenLimit)}
                          </div>
                          {tokenLimit !== null && (
                            <div className="w-full h-1.5 bg-[#333333] rounded-full overflow-hidden mt-1">
                              <div
                                className={`h-full transition-all duration-300 ${
                                  usagePercentage >= 90 ? 'bg-red-500' :
                                  usagePercentage >= 70 ? 'bg-yellow-500' :
                                  'bg-[#F4CC04]'
                                }`}
                                style={{ width: `${usagePercentage}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {canManageTokens() && (
                          <button
                            onClick={() => setTokenModalMember(member)}
                            className="p-1.5 text-[#F4CC04] hover:bg-[#F4CC04]/10 rounded-lg transition-colors"
                            title="Gerenciar tokens"
                          >
                            <Zap className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#888888]">
                      {new Date(member.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {!isOwner ? (
                          <>
                            <button
                              onClick={() => setEditingMember(member)}
                              disabled={deletingMemberId === member.user_id}
                              className="p-2 text-[#F4CC04] hover:bg-[#F4CC04]/10 rounded-lg disabled:opacity-50 transition-colors"
                              title="Editar permissões"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveMemberClick(member)}
                              disabled={deletingMemberId === member.user_id}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50 transition-colors"
                              title="Remover membro"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-[#888888] px-2 py-1" title="Permissões do proprietário são imutáveis">
                            Permissões imutáveis
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filteredMembers.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-[#888888]">
            Exibindo {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredMembers.length)} de {filteredMembers.length} membro{filteredMembers.length !== 1 ? 's' : ''}
            {searchTerm && ` (filtrado de ${members.length} total)`}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* Modals */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        onInviteSuccess={() => {
          setRefreshInvites(prev => prev + 1);
          setShowInviteModal(false);
        }}
      />

      {editingMember && (
        <EditMemberPermissionsModal
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          workspace={workspace}
          member={editingMember}
          onSuccess={() => {
            loadMembers();
            setEditingMember(null);
          }}
        />
      )}

      {/* Token Management Modal */}
      {tokenModalMember && (
        <TokenManagementModal
          isOpen={!!tokenModalMember}
          onClose={() => setTokenModalMember(null)}
          user={{
            id: tokenModalMember.user_id,
            name: tokenModalMember.user_name || tokenModalMember.user?.name || 'Desconhecido',
            email: tokenModalMember.user_email || tokenModalMember.user?.email || '',
            token_limit: tokenModalMember.token_limit || null,
            token_used: tokenModalMember.token_used || 0,
          } as User}
          onUpdateLimit={handleUpdateTokenLimit}
          onResetTokens={handleResetTokens}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMemberToDelete(null);
          setErrorMessage(null);
        }}
        onConfirm={handleRemoveMember}
        title="Remover Membro"
        message={`Tem certeza que deseja remover "${memberToDelete?.user_name || memberToDelete?.user?.name || 'este membro'}" do workspace?\n\nO membro perderá acesso a todos os recursos do workspace.`}
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
        isProcessing={deletingMemberId === memberToDelete?.user_id}
      />
    </div>
  );
};

export default WorkspaceMembersTab;
